import { isStoreId } from '#server/utils/parameterValidation';
import type { EvaluationContext } from '@openfeature/web-sdk';
import { ProvideNames } from '#shared/statics';
import type { DeeplinkResponse } from '~~/models/Deeplink';

interface GlobalAppContext {
    isAppUser?: boolean;
    appDeviceOS?: string;
    appVersion?: string;
    btPushNotifications?: number;
}

export const initializeAppContext = (): void => {
    const globalAppContext = getAppContext();

    if (useRequestHeaders()['bt-user-group']) {
        useUserGroup().value = useRequestHeaders()['bt-user-group'];
        setABContextVariable('usergroup', useRequestHeaders()['bt-user-group']);
    } else if(useABConfigCookie()?.value?.context?.usergroup) {
        useUserGroup().value = useABConfigCookie()?.value?.context?.usergroup;
    }

    const context: EvaluationContext = {
        ...useABConfigCookie().value?.context,
        targetingKey: useExpCookieIdentifier()?.value ?? 'bt_undefined',
        isappuser: globalAppContext.isAppUser,
        appdeviceos: globalAppContext.appDeviceOS,
        appversion: globalAppContext.appVersion,
        btpushnotifications: globalAppContext.btPushNotifications,
    };

    if (globalAppContext.isAppUser) {
        useAppSettingsCookie().value.isApp = true;
        useAppConfig().isAppUser = true;
    }
    if (globalAppContext.appDeviceOS) {
        useAppSettingsCookie().value.isNativeApp = true;
    }

    useABConfigCookie().value = { context: context };
};

export const getAppContext = (): GlobalAppContext => {
    const logger = useLogger('getAppContext');
    const route = useRoute()?.fullPath?.toLowerCase();
    const appContext: GlobalAppContext = {};

    const isAppDeployment: boolean = useRuntimeConfig()?.public?.isPWABuild || route.startsWith('/app');
    const isMobileDevice: boolean = route.includes('btdevice=android') || useDevice()?.isApplePWA;
    const isSpecificVersion: boolean = !!(useRoute()?.query?.bttargetversion || useRoute()?.query?.btversionenv);
    const isPushNotification: boolean = !!useRoute()?.query?.btpushnotificationid;
    const hasPushToken: boolean = !!(useRoute()?.query?.btpushtoken || useRoute()?.query?.token);

    if (isAppDeployment || isMobileDevice || isSpecificVersion || isPushNotification || hasPushToken) {
        appContext.isAppUser = true;
    }

    if (isMobileDevice) {
        appContext.appDeviceOS = route.includes('btdevice=android') ? 'android' : 'ios';
    }

    if (isSpecificVersion) {
        const version = useRoute().query.bttargetversion || useRoute()?.query?.btversionenv;
        appContext.appVersion = version?.toString() ?? '-';
    }

    if (isPushNotification) {
        const openFeatureContext = useNuxtApp()?.$openfeature?.getContext();
        const oldCount: number = openFeatureContext?.btpushnotifications ?? 0;

        appContext.btPushNotifications = oldCount + 1;
        useMarker('notification-opened');
        useGeneralTrick({
            eventType: 'NOTIFICATION_OPEN',
            sessionId: useCurrentSessionIdentifier()?.value,
            json: {
                btpushnotificationid: useRoute()?.query?.btpushnotificationid?.toString(),
                url: [useRoute()?.fullPath],
            },
        });
    }

    if (hasPushToken) {
        const pushToken = useRoute()?.query?.btpushtoken || useRoute()?.query?.token;
        try {
            useAppPushCookie().value = JSON.parse(pushToken?.toString());
        } catch (error) {
            logger.debug('error, using unparsed', error);
            useAppPushCookie().value = pushToken?.toString();
        }
    }

    return appContext;
};

export const initializeStore = async (): Promise<void> => {
    let storeId: string = ProvideNames.DEFAULT_CENTRAL_STORE.id;

    let branchId = useRoute().query.branch_id;
    branchId = branchId?.startsWith('e_') ? branchId : ('e_' + branchId)
    const storeSlug = useRoute()?.params?.storeslug || useRoute()?.path?.split('/markt/')?.at(-1)?.split('/')?.at(0);
    useLogger('storeInitializer').debug('storeSlug = ' + storeSlug + ' branchId= ' + branchId + ' type=' + (typeof branchId === 'string') );

    let overrideStoreCookie = false;
    if (branchId && typeof branchId === 'string' && isStoreId(branchId)) {
        useLogger('storeInitializer').info('Set storeCookie by branch_id', branchId);
        storeId = branchId;
        overrideStoreCookie = true;
    } else if (storeSlug && typeof storeSlug === 'string' && !ProvideNames.FINDER_SYNONYMS.includes(storeSlug?.toLowerCase())) {
        useLogger('storeInitializer').info('Set storeCookie by storeSlug', storeSlug);
        storeId = await getStoreIdBySlug(storeSlug);
        overrideStoreCookie = true;
    } else if (useStoreCookie().value) {
        storeId = useStoreCookie().value;
    } else {
        useLogger('storeInitializer').info('No store cookie found. Request auto assign.');
        const { data: storeFromFinder } = await useFetch('/api/storeFinder', {
            params: {
                autoAssignStore: true,
            },
            method: 'GET',
        });

        if (Array.isArray(storeFromFinder?.value) && storeFromFinder?.value.length > 0) {
            useLogger('storeInitializer').info(`Auto assign found store: ${storeFromFinder?.value?.at(0)?.store?.id}`);
            storeId = storeFromFinder?.value?.at(0)?.store?.id;
        }
        overrideStoreCookie = true;
    }

    if(overrideStoreCookie) useStoreCookieInitialized().value = true;
    await setStoreCookieAndData(storeId, overrideStoreCookie);
};

export const initializeStoreInApp = async (): Promise<void> => {
    let storeId: string = ProvideNames.DEFAULT_CENTRAL_STORE.id;
    let overrideStoreCookie = false;

    if (useStoreCookie()?.value === undefined || useStoreCookie()?.value === ProvideNames.DEFAULT_CENTRAL_STORE.id) {
        //#region <Deferred Deeplink Handling (EXSI-145)>
        useLogger('storeInitializer').debug('trying to get store from deeplink')
        const deeplink: DeeplinkResponse = await $fetch<DeeplinkResponse>('/api/deeplink', {
            headers: useRequestHeaders(),
        });
        if (deeplink?.branchId) {
            useLogger('storeInitializer').debug('deeplink found with branch:', deeplink.branchId);
            storeId = deeplink.branchId;
            overrideStoreCookie = true;
        }
        //#endregion
    } else {
        storeId = useStoreCookie().value;
    }

    await setStoreCookieAndData(storeId, overrideStoreCookie);
};

const setStoreCookieAndData = async (storeId: string, overrideStoreCookie: boolean = false): Promise<void> => {
    useLogger('storeInitializer').info(`Got storeId (${storeId}). Request store data from neo and set state.`);
    try {
        if (overrideStoreCookie) {
            useStoreCookie().value = storeId;
        }
        useCurrentStore().value = await $fetch<Store>('/api/store', {
            query: {
                storeId: storeId,
            },
        });
    } catch (error) {
        useLogger('storeInitializer').error(`Error while trying to fetch store with id: ${storeId}`, error);
        useStoreCookie().value = ProvideNames.DEFAULT_CENTRAL_STORE.id;
        useCurrentStore().value = await $fetch<Store>('/api/store', {
            query: {
                storeId: ProvideNames.DEFAULT_CENTRAL_STORE.id,
            },
        });
    }
}